<template>
  <b-card>
    <b-card-body id="customers-card-body">
      <b-button
        variant="primary"
        type="button"
        size="sm"
        class="mb-2"
        @click="createCustomerParameter()"
      >
        <feather-icon icon="PlusIcon" size="15" />
        <span>{{ t('Add New Customer Parameter') }}</span>
        <b-tooltip
          triggers="hover"
          target="default-filter-new-btn"
          :title="t('Create Customer Parameter')"
          :delay="{ show: 0, hide: 50 }"
        />
      </b-button>
      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :show-footer="true"
        :rows="rows"
        :columns="getColumns"
        :pagination-options="{
          enabled: true,
          jumpFirstOrLast: true,
          rowsPerPageLabel: t('Rows per page'),
          ofLabel: t('ofLabel'),
          nextLabel: t('nextLabel'),
          prevLabel: t('prevLabel'),
          dropdownAllowAll: false
        }"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'name'">
            {{ props.row.firstname || '-' }}
          </span>
          <span v-else-if="props.column.field === 'type'">
            <b-badge pill :variant="props.row.type === 0 ? 'secondary' : 'primary'">
              {{ props.row.type === 0 ? t('Warehouse') : t('Branch') }}
            </b-badge>
          </span>
          <span v-else-if="props.column.field === 'phone_number'">
            {{ props.row.phone_number || '-' }}
          </span>
          <span v-else-if="props.column.field === 'email'">
            {{ props.row.email || '-' }}
          </span>
          <span v-else-if="props.column.field === 'attention_name'">
            {{ props.row.authorizedName || '-' }}
          </span>
          <span v-else-if="props.column.field === 'status'">
            <b-badge pill :variant="props.row.status ? 'success' : 'danger'">
              {{ props.row.status ? t('Active') : t('Passive') }}
            </b-badge>
          </span>

          <span v-else-if="props.column.field === 'actions'">
            <span class="d-flex justify-content-center table-icon-buttons">
              <b-button
                :id="`edit-button-${props.row.id}`"
                variant="primary"
                type="button"
                size="sm"
                @click="detailParameter(props.row.id)"
              >
                <feather-icon icon="Edit3Icon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`edit-button-${props.row.id}`"
                  :title="`${t('Edit')}`"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
              <b-button
                :id="`delete-parameter-button-${props.row.id}`"
                variant="danger"
                type="button"
                size="sm"
                @click="deleteCustomerParameter(props.row.id)"
              >
                <feather-icon icon="XIcon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`delete-parameter-button-${props.row.id}`"
                  :title="t('Delete')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
            </span>
          </span>
        </template>
      </vue-good-table>
    </b-card-body>
  </b-card>
</template>

<script>
import { BBadge, BCard, BCardBody, BTooltip, BButton } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import useService from '@/auth/service/useService';
import store from '@/store/index';

export default {
  components: {
    VueGoodTable,
    BBadge,
    BCard,
    BCardBody,
    BTooltip,
    BButton,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent
  },
  mixins: [helper],

  data() {
    return {
      isLoading: false,
      rows: [],
      details: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      filterValues: {
        name: '',
        attention_name: '',
        status: '',
        email: '',
        created_at: '',
        updated_at: ''
      },
      refundData: null,
      refundSubmitting: false,
      refundPrice: '',
      refundAllPriceCheckbox: false
    };
  },

  computed: {
    direction() {
      if (store.state.appConfig.isRTL) {
        // eslint-disable-next-line vue/no-side-effects-in-computed-properties
        this.dir = true;
        return this.dir;
      }
      // eslint-disable-next-line vue/no-side-effects-in-computed-properties
      this.dir = false;
      return this.dir;
    },

    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      return [
        {
          label: this.$t('Description'),
          field: 'description'
        },
        {
          label: this.$t('Key'),
          field: 'key'
        },
        {
          label: this.$t('Type'),
          field: 'type'
        },
        {
          label: this.$t('Value'),
          field: 'value'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    }
  },

  async created() {
    this.loadItems();
  },

  methods: {
    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    loadItems() {
      const routerId = this.$route.params.id;

      const requestData = {
        customer: parseInt(routerId),
        ...this.filter
      };

      this.isLoading = true;

      useService
        .getAllCustomerParameters(requestData)
        .then(response => {
          this.rows = response.data;
          this.totalRecords = response.meta.total;
        })
        .finally(() => {
          this.isLoading = false;
        });

      // if(requestData.dateFrom && requestData.dateTo && (new Date(requestData.dateFrom) > new Date(requestData.dateTo))) {
      //   this.showErrorMessage(this.$t('Big Date Error'), this.$t('Unsuccessful'))
      //   return false
      // }
    },

    setFilter(data) {
      if (data) {
        this.filterValues = data;
        this.filter.page = 1;
        this.loadItems();
      }
    },

    deleteCustomerParameter(id) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Customer Parameter Delete Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await this.deleteCustomerParameterRequest(id)
            .then(response => {
              this.showSuccessMessage(
                `${response.message || this.$t('Customer Parameter Was Deleted')}`
              );

              this.filter.page = 1;
              this.loadItems();
            })
            .catch(messages => {
              if (typeof messages === 'object' && messages && messages.length) {
                messages.forEach(item => {
                  this.showErrorMessage(item.message);
                });
              }
            });
        }
      });
    },

    async deleteCustomerParameterRequest(id) {
      return useService.deleteCustomerParameter(id);
    },

    detailParameter(parameterId) {
      const customerId = this.$route.params.id;
      this.$router.push(`/customers/${customerId}/parameters/${parameterId}`);
    },

    createCustomerParameter() {
      const { id } = this.$route.params;
      this.$router.push(`/customers/${id}/parameters/create`);
    }
  },

  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
